import { endpoints } from '../config/endpoints';
import type { AuthSession } from '../store/authSession';

interface ManagementLoginResponse {
  token: string;
  refresh_token: string;
}

interface ManagementRefreshResponse {
  token: string;
}

function formData(values: Record<string, string>) {
  return new URLSearchParams(Object.entries(values)).toString();
}

async function managementRequest<T>(path: string, values: Record<string, string>): Promise<T> {
  const response = await fetch(`${endpoints.managementControl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData(values)
  });

  const payload = await response.json().catch(() => null) as ({ error?: string } & Partial<T>) | null;
  if (!response.ok) {
    throw new Error(payload?.error || 'Die Anfrage konnte nicht verarbeitet werden.');
  }
  if (!payload) throw new Error('Management Control hat keine Antwort geliefert.');
  return payload as T;
}

function sessionFromResponse(response: ManagementLoginResponse): AuthSession {
  if (!response.token || !response.refresh_token) throw new Error('Die Anmeldung hat keine gültige Sitzung geliefert.');
  return { accessToken: response.token, refreshToken: response.refresh_token };
}

export const authApi = {
  login: async ({ email, password }: { email: string; password: string }) =>
    sessionFromResponse(
      await managementRequest<ManagementLoginResponse>('/v1/login', { email, password })
    ),
  refresh: async (refreshToken: string) => {
    const response = await managementRequest<ManagementRefreshResponse>('/v1/login/refresh', { refresh_token: refreshToken });
    if (!response.token) throw new Error('Management Control hat kein neues Zugriffstoken geliefert.');
    return { accessToken: response.token, refreshToken };
  },
  requestPasswordRecovery: (email: string) =>
    managementRequest('/v1/login/recovery', { email }),
  resetPassword: ({ token, password }: { token: string; password: string }) =>
    managementRequest('/v1/login/reset', { token, password })
};
